import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fmtUSD } from "../lib/format";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Settings2,
  X,
  Link2,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import WalletActions from "../components/WalletActions";
import MultiSelectChips from "../components/MultiSelectChips";

const EMPTY = {
  name: "",
  location: "",
  linked_chime_ids: [],
  linked_paypal_ids: [],
  status: "active",
  balance: 0,
  notes: "",
};

const isStale = (iso) =>
  !iso || Date.now() - new Date(iso).getTime() > 7 * 24 * 60 * 60 * 1000;

export default function CashApps() {
  const [list, setList] = useState([]);
  const [chimes, setChimes] = useState([]);
  const [paypals, setPaypals] = useState([]);
  const [opts, setOpts] = useState({ statuses: ["active", "inactive", "frozen"] });
  const [loading, setLoading] = useState(true);
  const loadedOnceRef = useRef(false);

  const [filters, setFilters] = useState({
    q: "",
    status: "all",
    sort: "default",
    minBalance: "",
    staleOnly: false,
  });

  // CRUD dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [chimePickerOpen, setChimePickerOpen] = useState(false);
  const [chimeSearch, setChimeSearch] = useState("");
  const [updateBalanceFor, setUpdateBalanceFor] = useState(null);
  const [updateBalanceValue, setUpdateBalanceValue] = useState("");
  const [updateBalanceNotes, setUpdateBalanceNotes] = useState("");

  // Manage statuses dialog
  const [optsOpen, setOptsOpen] = useState(false);
  const [optsDraft, setOptsDraft] = useState([]);
  const [statusDraft, setStatusDraft] = useState("");

  const load = async (options = {}) => {
    const showLoading = options.showLoading ?? !loadedOnceRef.current;
    if (showLoading) setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.minBalance !== "") params.min_balance = Number(filters.minBalance);
      if (filters.staleOnly) params.not_updated_days = 7;
      const [c, ch, pp, o] = await Promise.all([
        api.get("/wallets", { params: { ...params, type: "cashapp" } }),
        api.get("/wallets", { params: { type: "chime" } }).catch(() => ({ data: [] })),
        api.get("/wallets", { params: { type: "paypal" } }).catch(() => ({ data: [] })),
        api.get("/wallet-options/cashapp").catch(() => ({ data: { statuses: ["active"] } })),
      ]);
      setList(c.data);
      setChimes(ch.data);
      setPaypals(pp.data);
      setOpts(o.data);
    } finally {
      loadedOnceRef.current = true;
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [filters]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, status: opts.statuses?.[0] || "active" });
    setOpen(true);
  };
  const openEdit = (w) => {
    setEditing(w);
    setForm({
      ...EMPTY,
      ...w,
      linked_chime_ids: w.linked_chime_ids || [],
      linked_paypal_ids: w.linked_paypal_ids || [],
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        balance: Number(form.balance) || 0,
        type: "cashapp",
      };
      if (editing) {
        await api.put(`/wallets/${editing.id}`, payload);
        toast.success("CashApp updated");
      } else {
        await api.post("/wallets", payload);
        toast.success("CashApp added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete CashApp "${w.name}"?`)) return;
    try {
      await api.delete(`/wallets/${w.id}`);
      toast.success("CashApp deleted");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openUpdateBalance = (wallet) => {
    setUpdateBalanceFor(wallet);
    setUpdateBalanceValue(String(wallet.balance ?? 0));
    setUpdateBalanceNotes("");
  };

  const submitUpdateBalance = async () => {
    try {
      const nb = Number(updateBalanceValue);
      if (Number.isNaN(nb)) return toast.error("Enter a valid balance");
      await api.post("/transactions", {
        wallet_id: updateBalanceFor.id,
        kind: "balance_update",
        new_balance: nb,
        notes: updateBalanceNotes,
      });
      toast.success("Balance updated");
      setUpdateBalanceFor(null);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openOpts = () => {
    setOptsDraft([...(opts.statuses || [])]);
    setStatusDraft("");
    setOptsOpen(true);
  };
  const addStatus = () => {
    const v = statusDraft.trim();
    if (!v) return;
    if (optsDraft.some((x) => x.toLowerCase() === v.toLowerCase()))
      return toast.message(`"${v}" already exists`);
    setOptsDraft([...optsDraft, v]);
    setStatusDraft("");
  };
  const saveOpts = async () => {
    try {
      await api.put("/wallet-options/cashapp", { statuses: optsDraft });
      toast.success("Statuses updated");
      setOptsOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const sortedList = useMemo(() => {
    if (filters.sort === "name-asc")
      return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (filters.sort === "name-desc")
      return [...list].sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    if (filters.sort === "balance-desc")
      return [...list].sort((a, b) => (b.balance || 0) - (a.balance || 0));
    if (filters.sort === "balance-asc")
      return [...list].sort((a, b) => (a.balance || 0) - (b.balance || 0));
    return list;
  }, [list, filters.sort]);

  const selectedChimes = useMemo(
    () => chimes.filter((c) => (form.linked_chime_ids || []).includes(c.id)),
    [chimes, form.linked_chime_ids]
  );
  const filteredChimes = useMemo(() => {
    const q = chimeSearch.trim().toLowerCase();
    if (!q) return chimes;
    return chimes.filter((c) =>
      [
        c.name,
        c.location,
        c.number_location,
        c.card_number,
        c.email,
        c.number,
        c.group,
        c.limits,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [chimes, chimeSearch]);
  const toggleChime = (id) => {
    const current = form.linked_chime_ids || [];
    setForm({
      ...form,
      linked_chime_ids: current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id],
    });
  };

  const totalBalance = useMemo(
    () => list.reduce((s, w) => s + (w.balance || 0), 0),
    [list]
  );
  const staleCount = useMemo(
    () => list.filter((w) => isStale(w.updated_at)).length,
    [list]
  );

  const activeFilters =
    (filters.q ? 1 : 0) +
    (filters.status !== "all" ? 1 : 0) +
    (filters.minBalance !== "" ? 1 : 0) +
    (filters.sort !== "default" ? 1 : 0) +
    (filters.staleOnly ? 1 : 0);

  return (
    <Layout
      title="CashApp"
      subtitle="Aggregator wallets, linkable to multiple Chimes"
      testId="cashapps-page"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={openOpts}
            className="h-9"
            data-testid="cashapp-manage-options-btn"
          >
            <Settings2 size={14} className="mr-1.5" /> Manage statuses
          </Button>
          <Button
            onClick={openCreate}
            className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="add-cashapp-btn"
          >
            <Plus size={14} className="mr-1.5" /> Add CashApp
          </Button>
        </div>
      }
    >
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            CashApps (filtered)
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">{list.length}</div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Total balance
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {fmtUSD(totalBalance)}
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Stale (7d+)
          </div>
          <div
            className={`font-mono text-[22px] font-medium mt-1 ${
              staleCount > 0 ? "text-amber-700" : "text-zinc-700"
            }`}
          >
            {staleCount}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="stat-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={13}
            strokeWidth={1.7}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search name, location, linked sources"
            className="h-9 pl-8"
            data-testid="cashapp-search-input"
          />
        </div>
        <Select
          value={filters.status}
          onValueChange={(v) => setFilters({ ...filters, status: v })}
        >
          <SelectTrigger className="h-9 w-[150px]" data-testid="cashapp-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {opts.statuses?.map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.sort}
          onValueChange={(v) => setFilters({ ...filters, sort: v })}
        >
          <SelectTrigger className="h-9 w-[170px]" data-testid="cashapp-filter-sort">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Sort: default</SelectItem>
            <SelectItem value="name-asc">Name: A → Z</SelectItem>
            <SelectItem value="name-desc">Name: Z → A</SelectItem>
            <SelectItem value="balance-desc">Balance: high → low</SelectItem>
            <SelectItem value="balance-asc">Balance: low → high</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <span className="text-[12px] text-zinc-500 ml-1">Bal ≥</span>
          <Input
            type="number"
            value={filters.minBalance}
            onChange={(e) => setFilters({ ...filters, minBalance: e.target.value })}
            className="h-9 w-[100px]"
            placeholder="500"
            data-testid="cashapp-filter-min-balance"
          />
        </div>
        <button
          onClick={() => setFilters({ ...filters, staleOnly: !filters.staleOnly })}
          className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-3 text-[12.5px] transition-colors ${
            filters.staleOnly
              ? "bg-amber-50 border-amber-300 text-amber-800"
              : "hairline bg-white text-zinc-700 hover:bg-zinc-50"
          }`}
          data-testid="cashapp-filter-stale"
        >
          Not updated 7d+
          {staleCount > 0 && (
            <span className="font-mono text-[11px] px-1.5 py-px rounded bg-amber-200 text-amber-900">
              {staleCount}
            </span>
          )}
        </button>
        {activeFilters > 0 && (
          <Button
            variant="ghost"
            className="h-9 text-zinc-600"
            onClick={() =>
              setFilters({
                q: "",
                status: "all",
                sort: "default",
                minBalance: "",
                staleOnly: false,
              })
            }
          >
            <X size={13} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Name</th>
                <th className="py-3 px-4 font-medium">Location</th>
	                <th className="py-3 px-4 font-medium">Linked sources</th>
	                <th className="py-3 px-4 font-medium">Status</th>
	                <th className="py-3 px-4 font-medium text-right">Balance</th>
	                <th className="py-3 px-4 font-medium text-right">Actions</th>
	              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
	                  <td colSpan={6} className="py-8 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              ) : sortedList.length === 0 ? (
                <tr>
	                  <td colSpan={6} className="py-10 text-center text-zinc-500">
                    {activeFilters > 0
                      ? "No CashApps match these filters."
                      : "No CashApps yet."}
                  </td>
                </tr>
              ) : (
                sortedList.map((w) => (
                  <tr
                    key={w.id}
                    className={`row-hover ${
                      isStale(w.updated_at) ? "bg-amber-50/40" : ""
                    }`}
                    data-testid={`cashapp-row-${w.id}`}
                  >
                    <td className="py-3 px-4 font-medium">{w.name}</td>
                    <td className="py-3 px-4 text-zinc-700">{w.location || "—"}</td>
                    <td className="py-3 px-4">
                      {(w.linked_chimes && w.linked_chimes.length > 0) ||
                      (w.linked_paypals && w.linked_paypals.length > 0) ? (
                        <div className="flex flex-wrap gap-1">
                          {w.linked_chimes?.map((c) => (
                            <span
                              key={`ch-${c.id}`}
                              className="inline-flex items-center gap-1 text-[11.5px] text-zinc-700 border hairline rounded px-1.5 py-0.5"
                              title="Chime"
                            >
                              <Link2 size={11} strokeWidth={1.7} />
                              {c.name}
                              {c.card_number && (
                                <span className="text-zinc-400 font-mono">
                                  ····{String(c.card_number).slice(-4)}
                                </span>
                              )}
                            </span>
                          ))}
                          {w.linked_paypals?.map((p) => (
                            <span
                              key={`pp-${p.id}`}
                              className="inline-flex items-center gap-1 text-[11.5px] text-violet-700 border border-violet-200 bg-violet-50 rounded px-1.5 py-0.5"
                              title="PayPal"
                            >
                              <Link2 size={11} strokeWidth={1.7} />
                              {p.name}
                              {p.card_number && (
                                <span className="text-violet-400 font-mono">
                                  ····{String(p.card_number).slice(-4)}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-zinc-400 text-[12px]">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex text-[11.5px] px-1.5 py-0.5 rounded border capitalize ${
                          w.status === "active"
                            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                            : "bg-zinc-100 border-zinc-200 text-zinc-600"
                        }`}
                      >
                        {w.status || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-right">
                      <button
                        type="button"
                        onClick={() => openUpdateBalance(w)}
                        className="font-mono text-right text-zinc-800 underline-offset-2 hover:text-sky-800 hover:underline"
                        title="Update balance"
                        data-testid={`quick-update-balance-${w.id}`}
                      >
                        {fmtUSD(w.balance)}
                      </button>
                    </td>
	                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <WalletActions wallet={w} onMutate={load} />
                        <button
                          onClick={() => openEdit(w)}
                          className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                          data-testid={`edit-cashapp-${w.id}`}
                        >
                          <Pencil size={14} strokeWidth={1.6} />
                        </button>
                        <button
                          onClick={() => remove(w)}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                          data-testid={`delete-cashapp-${w.id}`}
                        >
                          <Trash2 size={14} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="cashapp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit CashApp" : "Add CashApp"}
            </DialogTitle>
            <DialogDescription>
              Connect a CashApp to one or more Chimes (their cards).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>CashApp Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. $fastcash"
                data-testid="cashapp-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input
                value={form.location || ""}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Miami, FL"
                data-testid="cashapp-location-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger data-testid="cashapp-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opts.statuses?.map((g) => (
                    <SelectItem key={g} value={g} className="capitalize">
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Linked Chimes (cards)</Label>
              <div className="rounded-md border hairline bg-white p-2">
                <div className="flex flex-wrap gap-1.5 min-h-[32px]">
                  {selectedChimes.length === 0 ? (
                    <span className="text-[12px] text-zinc-400 self-center">
                      No Chimes linked yet
                    </span>
                  ) : (
                    selectedChimes.map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1 text-[12px] border hairline rounded px-1.5 py-0.5 bg-zinc-50"
                      >
                        {c.name}
                        {c.card_number && (
                          <span className="font-mono text-zinc-400">
                            ····{String(c.card_number).slice(-4)}
                          </span>
                        )}
                        <button
                          onClick={() => toggleChime(c.id)}
                          className="text-zinc-400 hover:text-rose-600"
                          data-testid={`cashapp-chime-multi-remove-${c.id}`}
                          type="button"
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-2 h-9 w-full"
                  onClick={() => {
                    setChimeSearch("");
                    setChimePickerOpen(true);
                  }}
                  data-testid="cashapp-chime-multi-add"
                >
                  <Plus size={13} className="mr-1.5" />
                  Search and add Chime
                </Button>
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Linked PayPal cards</Label>
              <MultiSelectChips
                value={form.linked_paypal_ids}
                options={paypals.map((p) => ({
                  id: p.id,
                  label: `${p.name}${p.card_number ? ` · ····${String(p.card_number).slice(-4)}` : ""}`,
                }))}
                onChange={(v) => setForm({ ...form, linked_paypal_ids: v })}
                placeholder="Add PayPal…"
                testId="cashapp-paypal-multi"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Opening balance (USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.balance}
                onChange={(e) => setForm({ ...form, balance: e.target.value })}
                disabled={!!editing}
                data-testid="cashapp-balance-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-cashapp-btn"
            >
              {editing ? "Save changes" : "Add CashApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={chimePickerOpen} onOpenChange={setChimePickerOpen}>
        <DialogContent className="max-w-4xl" data-testid="cashapp-chime-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add Chimes to CashApp</DialogTitle>
            <DialogDescription>
              Search Chimes by name, location, number location, card, group or limit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search
                size={14}
                strokeWidth={1.7}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <Input
                value={chimeSearch}
                onChange={(e) => setChimeSearch(e.target.value)}
                placeholder="Search Chime name, location, number location, card..."
                className="h-10 pl-9"
                data-testid="cashapp-chime-picker-search"
                autoFocus
              />
            </div>
            <div className="max-h-[430px] overflow-y-auto rounded-md border hairline">
              {filteredChimes.length === 0 ? (
                <div className="py-10 text-center text-sm text-zinc-500">
                  No Chimes match that search.
                </div>
              ) : (
                <div className="divide-y hairline">
                  {filteredChimes.map((c) => {
                    const selected = (form.linked_chime_ids || []).includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleChime(c.id)}
                        className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors ${
                          selected ? "bg-emerald-50/60" : "bg-white"
                        }`}
                        data-testid={`cashapp-chime-picker-row-${c.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-medium text-[13.5px]">
                              {c.name}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1.5 text-[11.5px] text-zinc-500">
                              {c.card_number && (
                                <span className="font-mono border hairline rounded px-1.5 py-0.5 bg-white">
                                  card ····{String(c.card_number).slice(-4)}
                                </span>
                              )}
                              {c.location && (
                                <span className="border hairline rounded px-1.5 py-0.5 bg-white">
                                  {c.location}
                                </span>
                              )}
                              {c.number_location && (
                                <span className="border hairline rounded px-1.5 py-0.5 bg-white">
                                  {c.number_location}
                                </span>
                              )}
                              {c.group && (
                                <span className="border hairline rounded px-1.5 py-0.5 bg-white">
                                  {c.group}
                                </span>
                              )}
                              {c.limits && (
                                <span className="border hairline rounded px-1.5 py-0.5 bg-white">
                                  {c.limits}
                                </span>
                              )}
                            </div>
                          </div>
                          <span
                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[12px] ${
                              selected
                                ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                                : "hairline bg-white text-zinc-600"
                            }`}
                          >
                            {selected ? (
                              <>
                                <Check size={13} /> Added
                              </>
                            ) : (
                              <>
                                <Plus size={13} /> Add
                              </>
                            )}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChimePickerOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update balance */}
      <Dialog open={!!updateBalanceFor} onOpenChange={(v) => !v && setUpdateBalanceFor(null)}>
        <DialogContent data-testid="update-balance-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Update balance</DialogTitle>
            <DialogDescription>
              Set the current balance for {updateBalanceFor?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Current balance</Label>
              <Input value={fmtUSD(updateBalanceFor?.balance || 0)} disabled className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>New balance (USD) *</Label>
              <Input
                type="number"
                step="0.01"
                value={updateBalanceValue}
                onChange={(e) => setUpdateBalanceValue(e.target.value)}
                data-testid="update-balance-value-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={updateBalanceNotes}
                onChange={(e) => setUpdateBalanceNotes(e.target.value)}
                placeholder="Optional"
                data-testid="update-balance-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateBalanceFor(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitUpdateBalance}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-update-balance-btn"
            >
              Save balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage statuses */}
      <Dialog open={optsOpen} onOpenChange={setOptsOpen}>
        <DialogContent data-testid="cashapp-options-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Manage CashApp statuses</DialogTitle>
            <DialogDescription>
              These statuses appear in the form and the filter bar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-1.5">
            <Input
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addStatus())}
              placeholder="e.g. shadow-banned"
              className="h-9"
              data-testid="cashapp-status-input"
            />
            <Button
              onClick={addStatus}
              className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              <Plus size={13} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {optsDraft.map((s, i) => (
              <span
                key={`${s}-${i}`}
                className="inline-flex items-center gap-1 text-[12px] border hairline rounded px-1.5 py-0.5 bg-zinc-50"
              >
                {s}
                <button
                  onClick={() => setOptsDraft(optsDraft.filter((_, idx) => idx !== i))}
                  className="text-zinc-400 hover:text-rose-600"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOptsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveOpts}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-cashapp-options-btn"
            >
              Save statuses
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
